import { useState, useMemo } from "react";
import classNames from "classnames";
import CopyButton from "../../../../../Components/common/CopyButton/CopyButton";
import FingerprintIcon from "../../../../../Components/Icon-SVG/FingerprintIcon";
import CustomBox from "../../../../../Components/UI/CustomBox/CustomBox/CustomBox";
import DetailButton from "../DetectionDetailPopup/DetailButton";
import useDetectionDetailPopup from "../DetectionDetailPopup/useDetectionDetailPopup";
import { TYPE_PRIORITY } from "./enrichedThreatTypes";

import "./styles.scss";

const EnrichedDataVisualization = ({ analysisDetails, enrichedMaliciousHashes }) => {
  const { findFileDetail, findDetailByHash, openDetail, popup } = useDetectionDetailPopup();

  const enrichedCount = analysisDetails?.enriched_malicious_count || 0;
  const [selected, setSelected] = useState(null); // null => show all with data

  // Group incoming data by reason
  const grouped = useMemo(() => {
    const byReason = (reason) =>
      Array.isArray(enrichedMaliciousHashes)
        ? enrichedMaliciousHashes.filter((h) => h.reason === reason)
        : [];
    return TYPE_PRIORITY.reduce((acc, { key, reason }) => {
      acc[key] = byReason(reason);
      return acc;
    }, {});
  }, [enrichedMaliciousHashes]);

  // If nothing’s selected, show only types that have data;
  // if one is selected, show that one (even if empty)
  const visibleTypes = useMemo(
    () =>
      TYPE_PRIORITY.filter(({ key }) => (selected ? selected === key : grouped[key].length > 0)),
    [selected, grouped],
  );

  return (
    <CustomBox className="etv">
      <h2 className="etv__title">Enriched Threat Data</h2>
      <div className="etv__container">
        {/* Summary + Tags */}
        <div className="etv__summary">
          <h4 className="etv__summary-title">Enrichment Summary</h4>
          <p className="etv__summary-desc">
            <strong>{enrichedCount}</strong> malicious items identified
          </p>
          <CustomBox className="etv__tags">
            {TYPE_PRIORITY.map(({ key, modifier, label }) => (
              <button
                key={key}
                type="button"
                className={classNames("etv__tag", `etv__tag--${modifier}`, {
                  "etv__tag--active": selected === key,
                })}
                onClick={() => setSelected(selected === key ? null : key)}>
                {grouped[key].length} {label}
              </button>
            ))}
          </CustomBox>
        </div>

        {/* Data Sections */}
        <div className="etv__data">
          <h4 className="etv__data-title">Enriched Threat Data</h4>

          {enrichedCount === 0 ? (
            <CustomBox className="etv__empty">
              <p className="etv__empty-text">No enriched threat data available</p>
            </CustomBox>
          ) : (
            visibleTypes.map(({ key, value, modifier, label }) => (
              <div key={key} className="etv__section">
                <div className="etv__scroll">
                  {grouped[key].length > 0 ? (
                    grouped[key].map((item, i) => {
                      const detail = findFileDetail(item.path) || findDetailByHash(item.hash);

                      return (
                        <CustomBox key={i} className="etv__detection">
                          <div className={classNames("etv__badge", `etv__badge--${modifier}`)}>
                            {value}
                          </div>
                          <div className="etv__hash-wrapper">
                            {item.hash && (
                              <CopyButton
                                title="Copy Hash"
                                itemToCopy={item.hash}
                                icon={FingerprintIcon}
                                iconHeight={20}
                                iconWidth={20}
                                successMessage="Hash Copied"
                                failureMessage="Copy Failed"
                              />
                            )}
                            <p className="etv__hash" title={item.hash || item.path || "Unknown"}>
                              {item.hash || item.path || "Unknown"}
                            </p>
                            {detail ? (
                              <DetailButton
                                title="Show details"
                                onClick={() => openDetail(detail)}
                              />
                            ) : null}
                          </div>
                          {item.path && <p className="etv__path">{item.path}</p>}
                        </CustomBox>
                      );
                    })
                  ) : (
                    <div className="etv__no-data">No {label.toLowerCase()} available</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {popup}
    </CustomBox>
  );
};

export default EnrichedDataVisualization;
